import { appLocale } from 'helpers/format';
/**
 * @function routeForEntity
 * @since 1.0.0
 * Route name for the record a reminder is attached to.
 */
export function routeForEntity(entity) {
    switch (entity) {
        case 'CLIENT':
            return 'client.update';
        case 'CONTACT':
            return 'contact.update';
        case 'DEAL':
            return 'deal.details';
        default:
            return null;
    }
}
/**
 * @function entityTypeLabel
 * @since 1.0.0
 */
export function entityTypeLabel(entity) {
    switch (entity) {
        case 'CLIENT':
            return t('Client');
        case 'CONTACT':
            return t('Contact');
        case 'DEAL':
            return t('Deal');
        default:
            return entity;
    }
}
/**
 * @function isReminderOverdue
 * @since 1.0.0
 */
export function isReminderOverdue(reminder, now = new Date()) {
    if (reminder.completedAt) {
        return false;
    }
    return new Date(reminder.dueAt).getTime() < now.getTime();
}
/**
 * @function startOfDay
 * @since 1.0.0
 */
function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
/**
 * @function dayOffset
 * @since 1.0.0
 * Whole calendar days between now and the date (negative in the past).
 */
export function dayOffset(value, now = new Date()) {
    const date = new Date(value);
    return Math.round((startOfDay(date).getTime() - startOfDay(now).getTime()) / 86400000);
}
/**
 * @function reminderBucket
 * @since 1.0.0
 * Section a reminder belongs to in a list grouped by urgency.
 */
export function reminderBucket(reminder, now = new Date()) {
    if (reminder.completedAt) {
        return 'completed';
    }
    if (isReminderOverdue(reminder, now)) {
        return 'overdue';
    }
    return dayOffset(reminder.dueAt, now) === 0 ? 'today' : 'upcoming';
}
/**
 * @function atNine
 * @since 1.0.0
 */
function atNine(date, days) {
    const result = startOfDay(date);
    result.setDate(result.getDate() + days);
    result.setHours(9, 0, 0, 0);
    return result;
}
/**
 * @function reminderDuePresets
 * @since 1.0.0
 * Shortcuts offered when setting or postponing a reminder. Day presets land
 * at 9 a.m., the start of a working day.
 */
export function reminderDuePresets(now = new Date()) {
    const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
    // Round up to the next quarter, the granularity of the reminder job
    inOneHour.setMinutes(Math.ceil(inOneHour.getMinutes() / 15) * 15, 0, 0);
    const daysToMonday = ((8 - now.getDay()) % 7) || 7;
    return [
        { key: 'hour', label: t('In 1 hour'), date: inOneHour },
        { key: 'tomorrow', label: t('Tomorrow morning'), date: atNine(now, 1) },
        { key: 'days', label: t('In 3 days'), date: atNine(now, 3) },
        { key: 'monday', label: t('Next Monday'), date: atNine(now, daysToMonday) },
        { key: 'week', label: t('In 1 week'), date: atNine(now, 7) }
    ];
}
/**
 * @function formatTime
 * @since 1.0.0
 */
export function formatTime(value) {
    return new Intl.DateTimeFormat(appLocale(), {
        hour: 'numeric',
        minute: '2-digit'
    }).format(new Date(value));
}
/**
 * @function formatFullDateTime
 * @since 1.0.0
 * Exact date, used as a tooltip next to relative dates.
 */
export function formatFullDateTime(value) {
    return new Intl.DateTimeFormat(appLocale(), {
        dateStyle: 'full',
        timeStyle: 'short'
    }).format(new Date(value));
}
/**
 * @function formatDayLabel
 * @since 1.0.0
 * "Aujourd'hui", "Hier", "Mardi 7 octobre", "3 octobre 2025"…
 */
export function formatDayLabel(value, now = new Date()) {
    const date = new Date(value);
    const days = dayOffset(date, now);
    let label;
    if (Math.abs(days) <= 1) {
        label = new Intl.RelativeTimeFormat(appLocale(), {
            numeric: 'auto'
        }).format(days, 'day');
    }
    else {
        label = new Intl.DateTimeFormat(appLocale(), {
            weekday: Math.abs(days) < 7 ? 'long' : undefined,
            day: 'numeric',
            month: 'long',
            year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric'
        }).format(date);
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
}
/**
 * @function formatDueDate
 * @since 1.0.0
 * Day close to now in words, then the time: "demain, 9 h 00",
 * "il y a 2 jours, 14 h 30", "15 oct., 9 h 00".
 */
export function formatDueDate(value, now = new Date()) {
    const date = new Date(value);
    const days = dayOffset(date, now);
    const day = Math.abs(days) < 7
        ? new Intl.RelativeTimeFormat(appLocale(), { numeric: 'auto' }).format(days, 'day')
        : new Intl.DateTimeFormat(appLocale(), {
            day: 'numeric',
            month: 'short',
            year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric'
        }).format(date);
    return day + ', ' + formatTime(date);
}
